import { useState } from 'react';
import { instrumentAPI } from '../../../../../services';
import './AddInstrument.css';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import './shared-form.css';

function AddInstrument({ onInstrumentAdded, isModal = false, onClose, validateSpreadPipettes }) {
  const initialState = {
    symbol: '',
    description: '',
    type: 'Forex',
    min_Order_Size: '1000',
    max_Order_Size: '5000000',
    price_Step: '0.00001',
    pips_Point: '0.0001',
    contract_Size: '1',
    decimals: '5',
    order_Quantity_Step: '1000',
    path: [],
    endpoint: '',
    spread_pipettes: '0',
  };

  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(isModal);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const handleSpreadPipettesChange = (e) => {
    const value = e.target.value;
    if (value === '' || validateSpreadPipettes(value)) {
      setFormData(prev => ({
        ...prev,
        spread_pipettes: value
      }));
    }
  };

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      setShowForm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate spread pipettes
    if (!validateSpreadPipettes(formData.spread_pipettes)) {
      setError('Spread pipettes must be a non-negative even number');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        // Convert symbol and type to uppercase
        symbol: formData.symbol.toUpperCase(),
        type: formData.type.toUpperCase(),
        path: formData.path.filter(p => p.trim() !== ''),
        // Convert numeric fields to strings to match API expectations
        contract_Size: String(formData.contract_Size || ''),
        min_Order_Size: String(formData.min_Order_Size || ''),
        max_Order_Size: String(formData.max_Order_Size || ''),
        price_Step: String(formData.price_Step || ''),
        pips_Point: String(formData.pips_Point || ''),
        spread_pipettes: String(formData.spread_pipettes || '0'),
        decimals: String(formData.decimals || ''),
        order_Quantity_Step: String(formData.order_Quantity_Step || '')
      };

      console.log('Submitting new instrument:', payload);
      const result = await instrumentAPI.add(payload);
      console.log('Add instrument result:', result);

      // Close the form/modal
      if (isModal && onClose) {
        onClose();
      } else {
        setShowForm(false);
      }
      
      setFormData(initialState);
      
      if (onInstrumentAdded) {
        onInstrumentAdded();
      }
    } catch (err) {
      console.error('Error adding instrument:', err);
      setError(err.message || 'Failed to add instrument. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-instrument-container">
      {!isModal && (
        <button 
          className="add-instrument-btn" 
          onClick={() => setShowForm(true)}
        >
          <i className="fas fa-plus"></i> Add New Instrument
        </button>
      )}

      {showForm && (
        <div className="add-instrument-overlay">
          <div className="add-instrument-form">
            <div className="form-header">
              <h4>Add New Instrument</h4>
              <button
                className="close-btn"
                onClick={handleClose}
                aria-label="Close form"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-content">
                <div className="form-card">
                  <div className="form-card-header">
                    <h5 className="form-card-title">Basic Information</h5>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="symbol">Symbol</label>
                      <div className="field-with-icon">
                        <i className="fas fa-tag field-icon"></i>
                        <input
                          id="symbol"
                          name="symbol"
                          value={formData.symbol}
                          onChange={handleChange}
                          required
                          placeholder="e.g. BTCUSD"
                        />
                      </div>
                      <div className="field-help">Unique identifier for this instrument</div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="type">Type</label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                      >
                        <option value="Forex">Forex</option>
                        <option value="Crypto">Crypto</option>
                        <option value="Stocks">Stocks</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <input
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Bitcoin vs US Dollar"
                    />
                  </div>
                </div>

                <div className="form-card">
                  <div className="form-card-header">
                    <h5 className="form-card-title">Trading Parameters</h5>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="min_Order_Size">Min Order Size (units)</label>
                      <input
                        id="min_Order_Size"
                        name="min_Order_Size"
                        type="number"
                        step="1"
                        value={formData.min_Order_Size}
                        onChange={handleChange}
                        required
                      />
                      <div className="field-help">Minimum quantity in pure units (e.g., 1000)</div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="max_Order_Size">Max Order Size (units)</label>
                      <input
                        id="max_Order_Size"
                        name="max_Order_Size"
                        type="number"
                        step="1"
                        value={formData.max_Order_Size}
                        onChange={handleChange}
                        required
                      />
                      <div className="field-help">Maximum quantity in pure units (e.g., 5000000)</div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="price_Step">Price Step</label>
                      <input
                        id="price_Step"
                        name="price_Step"
                        type="number"
                        step="0.00001"
                        value={formData.price_Step}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="pips_Point">Pips Point</label>
                      <input
                        id="pips_Point"
                        name="pips_Point"
                        type="number"
                        step="0.0001"
                        value={formData.pips_Point}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="contract_Size">Contract Size</label>
                      <input
                        id="contract_Size"
                        name="contract_Size"
                        type="number"
                        step="1"
                        value={formData.contract_Size}
                        onChange={handleChange}
                        disabled
                        style={{ background: '#f0f0f0', color: '#888' }}
                      />
                      <div className="field-help" style={{ color: '#888' }}>Fixed to 1 — quantities are now in pure units</div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="decimals">Decimals</label>
                      <input
                        id="decimals"
                        name="decimals"
                        type="number"
                        step="1"
                        value={formData.decimals}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="order_Quantity_Step">Order Quantity Step (units)</label>
                    <input
                      id="order_Quantity_Step"
                      name="order_Quantity_Step"
                      type="number"
                      step="1"
                      value={formData.order_Quantity_Step}
                      onChange={handleChange}
                      required
                    />
                    <div className="field-help">Step increment in pure units (e.g., 1000)</div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="spread_pipettes">Spread Pipettes*</label>
                    <input
                      type="number"
                      id="spread_pipettes"
                      name="spread_pipettes"
                      value={formData.spread_pipettes}
                      onChange={handleSpreadPipettesChange}
                      min="0"
                      step="2"
                      placeholder="Enter spread pipettes (even numbers only)"
                      required
                    />
                  </div>
                </div>

                <div className="form-card">
                  <div className="form-card-header">
                    <h5 className="form-card-title">Endpoint Configuration</h5>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="endpoint">Endpoint</label>
                    <div className="field-with-icon">
                      <i className="fas fa-link field-icon"></i>
                      <input
                        id="endpoint"
                        name="endpoint"
                        value={formData.endpoint}
                        onChange={handleChange}
                        placeholder="e.g. https://api.exchange.com/v1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Instrument'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
}

export default AddInstrument; 